/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useLayoutEffect, useState, useCallback } from 'react' // Import useLayoutEffect and useCallback
import deepEqual from 'fast-deep-equal'

import {
  BAR_HEIGHT,
  COL_WIDTH,
  ROUND_LABEL_HEIGHT,
  SLOT_HEIGHT,
} from './constants'
import { bracketType } from '@sportninja/common/constants/playoffTypes'

const CURVE_RADIUS = 8

const LineColumn = ({
  column,
  left = {},
  right = {},
  parentRef,
  selected,
  rounds,
  zoomLevel = 1,
}) => {
  // State to hold the calculated data needed for rendering SVG paths
  const [lineDrawData, setLineDrawData] = useState([])

  // Memoize the calculation function to avoid unnecessary recalculations if props haven't changed deeply
  // Note: This deep comparison might be heavy if rounds/left/right are very large.
  // Consider optimizing dependencies if performance issues arise.
  const calculateLines = useCallback(() => {
    // Due to an issue with the selected and zoomLevel, we need to skip the calculation if zoomLevel is not 1

    if (!parentRef?.current || !left?.pools?.length || !right?.pools?.length) {
      setLineDrawData([]) // Clear data if prerequisites aren't met
      return
    }

    const parentPosition = parentRef.current.getBoundingClientRect()
    const calculatedData = []

    left.pools.forEach((leftPool, idx) => {
      const { ref: leftRef, teamSlots, children } = leftPool
      const numTeams = teamSlots?.data?.length
      // const advance = leftPool?.children?.data?.length; // Keep if needed for startYSlotFactor logic
      const isUpperPool = leftPool?.bracket_position === bracketType.UPPER

      if (!children?.data) return // Skip if no children pointers

      children.data.forEach((pointer, advIdx) => {
        let rightPool = right.pools?.find((p) => p.id === pointer.id)
        let isSkipping = false

        // We only want to connect the pools if they are in the same bracket position (for upper)
        if (isUpperPool) {
          if (rightPool && rightPool?.bracket_position === bracketType.LOWER) {
            return // Don't connect Upper to Lower directly here
          } else if (!rightPool) {
            // Try finding the pool in subsequent rounds if it's not in the immediate 'right' prop
            rounds?.forEach((round) => {
              round.upperPools?.forEach((pool) => {
                if (
                  pool.id === pointer.id &&
                  pool?.bracket_position === bracketType.UPPER
                ) {
                  rightPool = pool
                }
              })
            })
            if (rightPool) {
              isSkipping = true // Found it further down the line
            }
          }
        }

        // If no right pool found after searching, skip
        if (!rightPool) return

        const startRect = leftRef?.current?.getBoundingClientRect()
        const endRect = rightPool?.ref?.current?.getBoundingClientRect()

        // Crucial check: Ensure all elements are rendered and measurable
        if (!startRect || !endRect) {
          // Element positions not ready yet. useLayoutEffect will run again if dependencies change.
          // Setting empty data prevents rendering stale/incorrect lines.
          // Consider adding a mechanism to retry if elements consistently fail to provide rects,
          // but useLayoutEffect often handles this better than setTimeout(0).
          console.warn(
            'LineColumn: Could not get bounding rects, skipping line draw for',
            leftPool.id,
            '->',
            rightPool.id
          )
          setLineDrawData([]) // Clear potentially stale data
          return // Skip this specific line
        }

        const startX = COL_WIDTH / 2
        // Adjust endX based on whether we are skipping columns
        // NOTE: This assumes COL_WIDTH is constant and skipping always jumps a fixed number (8?) columns.
        // Adjust the multiplier if skipping logic is more complex.
        const endX = isSkipping ? startX + COL_WIDTH * 7.5 : COL_WIDTH // Example: 8 columns total width means 7.5 * COL_WIDTH offset from startX

        // Simplified startYSlotFactor - adjust if more complex logic is needed
        const startYSlotFactor = (numTeams * SLOT_HEIGHT) / 2
        const endYSlotFactor =
          (rightPool?.teamSlots?.data?.length * SLOT_HEIGHT) / 2

        // Calculate Y positions relative to the parent container
        let startY =
          startRect.top +
          BAR_HEIGHT -
          ROUND_LABEL_HEIGHT +
          startYSlotFactor -
          parentPosition.top
        let endY =
          endRect.top +
          BAR_HEIGHT -
          ROUND_LABEL_HEIGHT +
          endYSlotFactor -
          parentPosition.top

        let endAboveStart = endY < startY
        // Don't swap startY/endY here, handle drawing direction in path logic

        const isHighlighted =
          deepEqual(leftPool?.id, selected?.id) ||
          deepEqual(rightPool?.id, selected?.id)

        const style = {
          stroke: isHighlighted ? colors.PRIMARY : 'rgb(255, 255, 255)',
          strokeWidth: isHighlighted ? 4 : 2,
          zIndex: isHighlighted ? 2 : 1,
          fill: 'none',
        }

        const radius = startY === endY ? 0 : CURVE_RADIUS
        const uniqueId = `${column}-${idx}-${advIdx}` // Unique ID for potential <use> element

        // --- Generate Path Data ---
        // Simpler single path approach (adjust if needed)
        // M = Move to
        // H = Horizontal line to
        // A = Arc (rx ry x-axis-rotation large-arc-flag sweep-flag x y)
        // V = Vertical line to
        let d = `M 0,${startY} H ${startX - radius}` // Start H line

        if (radius > 0) {
          // First curve: Draw an arc from the current point to (startX, startY±radius)
          // Parameters: radius x, radius y, x-axis-rotation, large-arc-flag, sweep-flag, end x, end y
          // sweep-flag: 0 = counter-clockwise, 1 = clockwise (depends on if end is above start)
          d += ` A ${radius},${radius} 0 0 ${endAboveStart ? 0 : 1} ${startX},${
            startY + (endAboveStart ? -radius : radius)
          }`

          // Vertical line: Connect the end of first curve to the start of second curve
          // Adjust the end point to leave room for the second curve
          d += ` V ${endY + (endAboveStart ? radius : -radius)}`

          // Second curve: Draw an arc from the vertical line to the horizontal position
          // This creates a smooth transition to the horizontal line
          d += ` A ${radius},${radius} 0 0 ${endAboveStart ? 1 : 0} ${
            startX + radius
          },${endY}`
        } else {
          // For straight lines (when startY equals endY)
          // Simply move horizontally to the center X position
          d += ` H ${startX}`

          // Then vertically to the end Y position (if needed)
          // This should rarely execute since radius=0 means startY=endY
          if (startY !== endY) {
            d += ` V ${endY}`
          }
        }
        // Final horizontal line
        d += ` H ${endX}`

        calculatedData.push({
          id: uniqueId,
          d: d,
          style: style,
          isHighlighted: isHighlighted,
          leftPoolId: leftPool.id, // Keep IDs for the <use> logic
          rightPoolId: rightPool.id,
        })
      })
    })

    // Only update state if the calculated data is different from the current state
    // Using deepEqual here, could be optimized if needed
    if (!deepEqual(calculatedData, lineDrawData)) {
      setLineDrawData(calculatedData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    column,
    left,
    right,
    parentRef,
    selected,
    rounds,
    lineDrawData, // Include lineDrawData to compare in useCallback for deepEqual check
    // Note: Including lineDrawData might cause loops if not careful.
    // Alternatively, manage the deepEqual check outside useCallback or use a different state update pattern.
    // Let's remove lineDrawData from deps and rely on the deepEqual check before setLineDrawData
  ])

  // Use useLayoutEffect for DOM measurements
  useLayoutEffect(() => {
    // Call the calculation function
    calculateLines()
    // Dependencies: Recalculate when these change.
    // Using deepEqual inside calculateLines helps prevent unnecessary updates
    // if object identities change but values don't.
  }, [calculateLines]) // Dependency array includes the memoized function

  // --- Rendering ---
  // Filter highlighted items first for the <use> elements trick to bring them to the front
  const highlightedItems = lineDrawData.filter((item) => item.isHighlighted)
  const nonHighlightedItems = lineDrawData.filter((item) => !item.isHighlighted)

  return (
    <div
      css={css`
        min-width: ${COL_WIDTH}px;
        width: ${COL_WIDTH}px;
        position: relative; /* Needed for absolute positioning of SVG if required */
        /* Ensure height accommodates content */
      `}
    >
      {/* Header remains the same */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          height: ${ROUND_LABEL_HEIGHT}px;

          .left,
          .right {
            background-color: ${colors.HEADER};
            flex-basis: 48%;
          }
        `}
      >
        <div className='left' />
        <div className='right' />
      </div>

      {/* SVG Container */}
      <svg
        // Make SVG cover the necessary area, potentially extending beyond COL_WIDTH if isSkipping=true
        // You might need to adjust width/height/viewBox dynamically or use a large enough fixed size
        // overflow: visible allows drawing outside the nominal SVG box
        css={css`
          position: absolute; // Position relative to the parent div
          top: ${ROUND_LABEL_HEIGHT}px; // Position below the header
          left: 0;
          width: 100%; // Start with column width
          height: 100%; // Adjust height as needed, or make it large
          overflow: visible;
          pointer-events: none; // Prevent SVG from intercepting mouse events unless needed
        `}
        // Consider adding a viewBox if scaling becomes complex
        // viewBox={`0 0 ${COL_WIDTH} ${calculatedHeight}`}
      >
        {/* Render non-highlighted lines first */}
        {nonHighlightedItems.map((item) => (
          <path
            key={item.id}
            id={item.id} // ID for potential <use> reference
            d={item.d}
            style={item.style}
            className='line-column'
          />
        ))}

        {/* Render highlighted lines last (or use <use>) */}
        {/* Using <use> ensures the exact same path is rendered again on top */}
        {highlightedItems.map((item) => (
          <path
            key={item.id}
            id={item.id} // ID for potential <use> reference
            d={item.d}
            style={item.style}
            className='line-column highlighted'
          />
          // Alternatively, using <use> if preferred (renders slightly differently in dev tools):
          // <use
          //   key={`use-${item.id}`}
          //   xlinkHref={`#${item.id}`}
          //   style={item.style} // Apply highlighting style to the <use> element
          //   className='line-column highlighted'
          // />
        ))}
      </svg>
    </div>
  )
}

export default LineColumn
